import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { manufacturingApi, type BomDto, type BomComponentDto } from '../api/manufacturing';
import { FeatureGate } from '../components/FeatureGate';

export default function BomListPage() {
  const navigate = useNavigate();

  const { data: boms = [], isLoading } = useQuery({
    queryKey: ['boms'],
    queryFn: manufacturingApi.listBoms,
    staleTime: 30_000,
  });

  return (
    <FeatureGate feature="manufacturingModule" requiredPlan="Gold" upgradeMessage="Manufacturing module requires a Gold plan.">
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/manufacturing')}>
              ← Orders
            </button>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600 }}>Bills of Materials</h2>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/manufacturing/boms/new')}>
            + New BOM
          </button>
        </div>

        {isLoading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>
        ) : boms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
            <p style={{ fontSize: '0.875rem' }}>No BOMs created yet.</p>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/manufacturing/boms/new')}>
              Create your first BOM
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {boms.map((bom: BomDto) => (
              <div
                key={bom.id}
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem', cursor: 'pointer' }}
                onClick={() => navigate(`/manufacturing/boms/${bom.id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{bom.product?.name ?? 'Unknown'}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.2rem' }}>
                      v{bom.version} · {bom.components.length} component{bom.components.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <span className={`badge ${bom.isActive ? 'badge-green' : 'badge-draft'}`} style={{ fontSize: '0.75rem' }}>
                    {bom.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {bom.components.length > 0 && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {bom.components.slice(0, 5).map((c: BomComponentDto, i: number) => (
                      <span
                        key={i}
                        style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', background: 'var(--surface-alt)', borderRadius: '4px', color: 'var(--text-2)' }}
                      >
                        {c.component?.name ?? c.componentProductId} × {Number(c.qty).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                        {c.wastagePercent ? ` (+${c.wastagePercent}% waste)` : ''}
                      </span>
                    ))}
                    {bom.components.length > 5 && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>+{bom.components.length - 5} more</span>
                    )}
                  </div>
                )}

                {bom.notes && (
                  <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{bom.notes}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </FeatureGate>
  );
}
